import * as React from "react";
import { useTranslation } from "react-i18next";
import { useApi, useNavigation } from "../../hooks";
import { useAuthStore } from "../../stores";
import { Tools } from "../../utils";
import { Tags } from "../../libs";
import { TagsEvent } from "../../libs/tags";
import type { ModalRef } from "../../components/Modal";
import type { ButtonRef } from "../../components/Button";

export function useViewModel() {
  const { t } = useTranslation();
  const { navigate, state } = useNavigation();
  const login = useAuthStore((s) => s.login);
  const { fetch } = useApi();

  const [email, setEmail] = React.useState("");
  const modalRef = React.useRef<ModalRef>(null);
  const buttonRef = React.useRef<ButtonRef>(null);
  const isSubmitDisabled = React.useMemo(
    () => !Tools.isValidEmail(email),
    [email],
  );

  const onLogin = React.useCallback(
    async (data?: any) => {
      Tags.event(TagsEvent.LoginSubmitted);
      try {
        const result = await fetch(() => login(email, data?.params ?? {}), {
          successMsg: (result: any) =>
            !("isTCAccepted" in result) && t("login.successMsg", { email }),
        });
        if ("isTCAccepted" in result && !result.isTCAccepted) {
          modalRef.current?.open();
          return;
        } else {
          navigate(`/verify-email`, {
            state,
            searchParams: {
              ...result,
              email: encodeURIComponent(email),
            },
          });
        }
      } catch {}
    },
    [email, fetch, login, navigate, t, state],
  );

  const acceptTermsAndContinue = React.useCallback(() => {
    Tags.event(TagsEvent.AcceptTerms);
    modalRef.current?.close();
    buttonRef.current?.click({ params: { isTCAccepted: "true" } });
  }, []);

  return {
    t,
    email,
    setEmail,
    modalRef,
    buttonRef,
    onLogin,
    isSubmitDisabled,
    acceptTermsAndContinue,
  };
}

export type LoginViewModel = ReturnType<typeof useViewModel>;
